/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {resolveListReadOnly} from '../routing/adn-router-helper-core';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {OrderApiService, OrderModel} from './order-api.service';
import {OrderCopyModalComponent} from './order-copy-modal.component';
import {OrderItemPageComponent} from './order-item-page.component';
import {OrderListPageComponent} from './order-list-page.component';

export function orderStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(OrderApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => resolveListReadOnly(false, 'orders', permissions);
  const rh = adnRouterHelper.createRouterAndChildHelper(
    'orders.order', OrderApiService, LineItemApiService, 'app.adv', 'app'
  );
  const copyConfig = {
    modalComponent: OrderCopyModalComponent,
    stateUrl: 'app.adv.orders.order',
    lcase: 'order',
    ucase: 'Order'
  };
  const titleOptions = {
    objectTypeKey: 'Order',
    crumbParams: [{type: 'Advertiser', func: 'canBeShownAdvertiser'}]
  };
  const confirmation = {
    warningMessage: "The {{action}} of the Order will also stop any of the Order's Line Items from serving ads.",
    object: 'Order'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {
      readOnly,
      sectionParam: () => 'orders'
    },
    views: {'content@app': {component: OrderListPageComponent, bindings: {readOnly: 'listReadOnly'}}}
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      orderPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<OrderModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []},
            true, readOnly(), api, 'Order', 'name', copyConfig, undefined, titleOptions, confirmation
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Order', 'name', copyConfig, undefined, titleOptions, confirmation
        ));
      },
      model: function(orderPage: SignalAdminResourcePageState<OrderModel>) {
        return orderPage.model();
      }
    },
    views: {
      'content@app': {component: OrderItemPageComponent, bindings: {state: 'orderPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'orderPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'orderPage'}}
    }
  }));
  return states.build();
}
